import { useState } from 'react'
import { useNavigate } from 'react-router'
import { api, call } from '@/api/client'
import type { ApiFailure } from '@/api/client'
import { useOnboarding } from './context'
import { pathFor } from './steps'
import type { StepKey } from './steps'

/**
 * Settles one of the five settings steps after its form has saved through
 * the setting's own endpoint, then moves the wizard on.
 *
 * Settings consolidation (2026-09-15). The value is never sent here — the
 * form's `onSubmit` made the same `PUT` the Settings panel makes, and
 * `POST /onboarding/steps/{step}/done` records only that the step is answered.
 * A failure between the two leaves the value saved and the step pending, which
 * the tenant resolves by pressing Continue again.
 *
 * The wizard finishes here rather than on brand voice now: when the server
 * says nothing is outstanding it leaves the cursor on the step just settled,
 * and `may_complete` is what `Finish setup` asks — so that pair is the signal
 * to go to `/onboarding/done`.
 */
export function useSettleStep(step: StepKey): {
  settle: () => Promise<void>
  failure: ApiFailure | null
} {
  const { adopt } = useOnboarding()
  const navigate = useNavigate()
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  return {
    failure,
    settle: async () => {
      const result = await call(() =>
        api.POST('/api/v1/onboarding/steps/{step}/done', { params: { path: { step } } }),
      )
      if (!result.ok) {
        setFailure(result.failure)
        return
      }
      adopt(result.data)
      const next = result.data
      void navigate(
        next.may_complete && next.current_step === step
          ? '/onboarding/done'
          : pathFor(next.current_step),
      )
    },
  }
}
